/**
 * Camera permission hook for FVrad
 * Handles check/request logic and UI fallback for future radiation proxy (vision-camera) in V1.1
 * Handles iOS/Android differences via react-native-permissions
 */

import { useState, useEffect, useCallback } from 'react';
import { Platform } from 'react-native';
import {
  check,
  request,
  openSettings,
  PERMISSIONS,
  RESULTS,
} from 'react-native-permissions';
import type { PermissionStatus } from 'react-native-permissions';

const CAMERA_PERMISSION =
  Platform.OS === 'ios' ? PERMISSIONS.IOS.CAMERA : PERMISSIONS.ANDROID.CAMERA;

export type CameraPermissionStatus =
  | 'checking'
  | 'granted'
  | 'denied'
  | 'blocked'
  | 'unavailable';

export type UseCameraPermissionResult = {
  status: CameraPermissionStatus;
  isGranted: boolean;
  isBlocked: boolean;
  isRequestable: boolean;
  checkPermission: () => Promise<void>;
  requestPermission: () => Promise<void>;
  openAppSettings: () => Promise<void>;
};

export function useCameraPermission(): UseCameraPermissionResult {
  const [status, setStatus] = useState<CameraPermissionStatus>('checking');

  const mapResult = useCallback((result: PermissionStatus): CameraPermissionStatus => {
    switch (result) {
      case RESULTS.GRANTED:
      case RESULTS.LIMITED:
        return 'granted';
      case RESULTS.DENIED:
        return 'denied';
      case RESULTS.BLOCKED:
        return 'blocked';
      case RESULTS.UNAVAILABLE:
      default:
        return 'unavailable';
    }
  }, []);

  const checkPermission = useCallback(async () => {
    try {
      const result = await check(CAMERA_PERMISSION);
      setStatus(mapResult(result));
    } catch {
      setStatus('unavailable');
    }
  }, [mapResult]);

  const requestPermission = useCallback(async () => {
    try {
      const result = await request(CAMERA_PERMISSION);
      setStatus(mapResult(result));
    } catch {
      setStatus('unavailable');
    }
  }, [mapResult]);

  const openAppSettings = useCallback(async () => {
    try {
      await openSettings();
    } catch {
      // Ignore - settings may not be openable in some contexts
    }
  }, []);

  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  return {
    status,
    isGranted: status === 'granted',
    isBlocked: status === 'blocked',
    isRequestable: status === 'denied',
    checkPermission,
    requestPermission,
    openAppSettings,
  };
}
